import { type FC, useContext, useMemo } from "react";

import { flow } from "fp-ts/lib/function";

import { useFeatureGates } from "../../../cof-feature-gates/hooks";
import { sortOrganizationContainers } from "../../../core/context";
import { type OrganizationContainer } from "../../../core/models";
import { CofConfigId, type ResourceContainer } from "../../../gql-generated/types";
import { UserInfoContext } from "../../../shared/api/contexts";
import { ContainerDisplay } from "../../../shared/components";
import { useSelectedOrganizationContainer } from "../../../shared/hooks";

import { ContainersNav, ListOfOrgContainers } from "./LensContainers.styles";

type ContainerUnion = OrganizationContainer | ResourceContainer;

interface OrgContainersV3Props {
    organizationContainers: OrganizationContainer[] | ResourceContainer[];
    cancelPopover: () => void;
    searchText: string;
}

const hasLensFolders = (org: ContainerUnion): org is OrganizationContainer => {
    return "lensFolders" in org;
};

const filterContainersBySearchText =
    (searchText: string, skipSearchByLensFolders: boolean) =>
    (organizationContainers: ContainerUnion[]): ContainerUnion[] => {
        return organizationContainers.filter((organization) => {
            const searchTextLower = searchText.toLocaleLowerCase();
            let childContainersHaveText = false;
            if (!skipSearchByLensFolders && hasLensFolders(organization)) {
                childContainersHaveText = organization.lensFolders.some(
                    (container) =>
                        container.name.toLocaleLowerCase().includes(searchTextLower) ||
                        container.id.includes(searchTextLower),
                );
            }

            return (
                childContainersHaveText ||
                organization.name.toLocaleLowerCase().includes(searchTextLower) ||
                organization.id.includes(searchTextLower)
            );
        });
    };

const filterEmptyOrganizationContainers =
    (shouldSkip: boolean) =>
    (organizationContainers: ContainerUnion[]): ContainerUnion[] =>
        shouldSkip
            ? organizationContainers
            : organizationContainers.filter(
                  (organization) => hasLensFolders(organization) && organization.lensFolders.length > 0,
              );

export const OrgContainersV3: FC<OrgContainersV3Props> = ({ organizationContainers, searchText }) => {
    const { orgId } = useSelectedOrganizationContainer();
    const userInfo = useContext(UserInfoContext);
    const { configs } = useFeatureGates();
    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;
    const skipFilterEmptyOrgs = configs[CofConfigId.MyLensesPortalUnification].enabled || isMyLensesOrgsV2Enabled;

    const organizationContainersFiltered: ContainerUnion[] = useMemo(
        () =>
            flow(
                filterContainersBySearchText(searchText, isMyLensesOrgsV2Enabled),
                filterEmptyOrganizationContainers(skipFilterEmptyOrgs),
                sortOrganizationContainers(userInfo?.data?.getUserProfile.defaultOrganizationId),
            )(organizationContainers),
        [
            userInfo?.data?.getUserProfile.defaultOrganizationId,
            organizationContainers,
            searchText,
            skipFilterEmptyOrgs,
            isMyLensesOrgsV2Enabled,
        ],
    );

    return (
        <ContainersNav>
            <ListOfOrgContainers>
                {organizationContainersFiltered.map((container) => (
                    <ContainerDisplay
                        key={container.id}
                        container={container}
                        selectedContainerId={orgId ?? ""}
                        orgId={container.id}
                    />
                ))}
            </ListOfOrgContainers>
        </ContainersNav>
    );
};
